import {useState, useRef, useEffect, useId} from 'react'
import {Calendar, ChevronLeft, ChevronRight, X} from 'lucide-react'
import {parseLocalDate} from '../../utils/helpers'

interface DatePickerDropdownProps {
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  label?: string
  showPresets?: boolean
  minDate?: string | null
  className?: string
  compact?: boolean
}

function formatDisplayDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number)
  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ]
  return `${day} ${months[month - 1]} ${year}`
}

function toDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function normalizeDateValue(value?: string | null): string | null {
  if (!value) return null
  const parsed = parseLocalDate(value)
  return Number.isNaN(parsed.getTime()) ? null : toDateStr(parsed)
}

function addDays(dateStr: string | null, days: number): string {
  const base = dateStr ? new Date(dateStr + 'T12:00:00') : new Date()
  base.setDate(base.getDate() + days)
  return toDateStr(base)
}

function getToday(): string {
  return toDateStr(new Date())
}

function getMonthGrid(year: number, month: number): (number | null)[][] {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const weeks: (number | null)[][] = []
  let currentWeek: (number | null)[] = []

  for (let i = 0; i < firstDay; i++) {
    currentWeek.push(null)
  }

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day)
    if (currentWeek.length === 7) {
      weeks.push(currentWeek)
      currentWeek = []
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null)
    }
    weeks.push(currentWeek)
  }

  return weeks
}

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

export default function DatePickerDropdown({
  value,
  onChange,
  placeholder = 'Selecionar data',
  showPresets = true,
  minDate,
  className = '',
  compact = false,
}: DatePickerDropdownProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const calendarId = useId()
  const normalizedValue = normalizeDateValue(value)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const today = getToday()

  const initialDate = normalizedValue ? new Date(normalizedValue + 'T12:00:00') : new Date()
  const [viewYear, setViewYear] = useState(initialDate.getFullYear())
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth())

  useEffect(() => {
    if (open && normalizedValue) {
      const d = parseLocalDate(normalizedValue)
      setViewYear(d.getFullYear())
      setViewMonth(d.getMonth())
    } else if (open && !normalizedValue) {
      const now = new Date()
      setViewYear(now.getFullYear())
      setViewMonth(now.getMonth())
    }
  }, [open, normalizedValue])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  function selectDate(dateStr: string) {
    if (isDayDisabled(dateStr)) return
    onChange(dateStr)
    setOpen(false)
    triggerRef.current?.focus()
  }

  function clearDate(e: React.MouseEvent) {
    e.stopPropagation()
    onChange(null)
    setOpen(false)
  }

  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear(viewYear - 1)
    } else {
      setViewMonth(viewMonth - 1)
    }
  }

  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear(viewYear + 1)
    } else {
      setViewMonth(viewMonth + 1)
    }
  }

  function isDayDisabled(dayStr: string): boolean {
    const minimum = normalizeDateValue(minDate)
    return !!minimum && dayStr < minimum
  }

  const grid = getMonthGrid(viewYear, viewMonth)

  const presets = [
    {label: 'Hoje', getValue: () => getToday()},
    {label: 'Amanhã', getValue: () => addDays(null, 1)},
    {label: '+2 dias', getValue: () => addDays(null, 2)},
    {label: '+3 dias', getValue: () => addDays(null, 3)},
    {label: '+1 semana', getValue: () => addDays(null, 7)},
    {label: '+2 semanas', getValue: () => addDays(null, 14)},
  ]

  const isToday = normalizedValue === today

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault()
          event.stopPropagation()
          setOpen(false)
          triggerRef.current?.focus()
        }
      }}
    >
      <div className='relative'>
        <button
          type='button'
          ref={triggerRef}
          aria-expanded={open}
          aria-controls={calendarId}
          aria-label={`${placeholder}${
            normalizedValue ? `: ${formatDisplayDate(normalizedValue)}` : ''
          }`}
          onClick={() => setOpen(!open)}
          className={`flex items-center gap-2 w-full rounded-xl border transition-colors text-left ${
            compact ? 'pl-2.5 pr-8 py-1.5 text-xs' : 'pl-3 pr-9 py-2 text-sm'
          } ${
            open
              ? 'border-brand-400 ring-2 ring-brand-100 bg-white'
              : 'border-slate-200 hover:border-slate-300 bg-white'
          } ${value ? 'text-slate-700' : 'text-slate-400'}`}
        >
          <Calendar
            size={compact ? 14 : 16}
            className={`shrink-0 ${value ? 'text-brand-500' : 'text-slate-400'}`}
          />
          <span className='flex-1 truncate'>
            {normalizedValue ? formatDisplayDate(normalizedValue) : placeholder}
          </span>
          {isToday && (
            <span className='shrink-0 text-[10px] font-semibold text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded-md'>
              Hoje
            </span>
          )}
        </button>
        {value && (
          <button
            type='button'
            onClick={clearDate}
            className='absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-500'
            aria-label='Limpar data'
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div
          id={calendarId}
          role='group'
          aria-label='Calendário'
          className='absolute top-full left-0 mt-1.5 z-[60] w-[280px] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden animate-fade-in'
        >
          {showPresets && (
            <div className='p-2 border-b border-slate-100'>
              <div className='grid grid-cols-3 gap-1'>
                {presets.map((preset) => {
                  const presetVal = preset.getValue()
                  const disabled = isDayDisabled(presetVal)
                  const isSelected = normalizedValue === presetVal
                  return (
                    <button
                      key={preset.label}
                      type='button'
                      disabled={disabled}
                      onClick={() => selectDate(presetVal)}
                      className={`px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        disabled
                          ? 'text-slate-300 cursor-not-allowed'
                          : isSelected
                          ? 'bg-brand-500 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {preset.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className='p-3'>
            <div className='flex items-center justify-between mb-3'>
              <button
                type='button'
                onClick={prevMonth}
                className='p-1 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors'
                aria-label='Mês anterior'
              >
                <ChevronLeft size={16} />
              </button>
              <span className='text-sm font-semibold text-slate-800'>
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              <button
                type='button'
                onClick={nextMonth}
                className='p-1 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors'
                aria-label='Próximo mês'
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div className='grid grid-cols-7 gap-0'>
              {WEEKDAYS.map((d, i) => (
                <div
                  key={i}
                  className='text-center text-[10px] font-semibold text-slate-400 uppercase py-1'
                >
                  {d}
                </div>
              ))}

              {grid.flat().map((day, i) => {
                if (day === null) {
                  return <div key={`empty-${i}`} className='h-8' />
                }

                const dayStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(
                  day
                ).padStart(2, '0')}`
                const isSelected = normalizedValue === dayStr
                const isTodayDay = dayStr === today
                const disabled = isDayDisabled(dayStr)

                return (
                  <button
                    key={dayStr}
                    type='button'
                    disabled={disabled}
                    aria-label={`${day} de ${MONTH_NAMES[viewMonth]} de ${viewYear}`}
                    aria-pressed={isSelected}
                    aria-current={isTodayDay ? 'date' : undefined}
                    onClick={() => selectDate(dayStr)}
                    className={`h-8 w-full flex items-center justify-center text-xs rounded-lg transition-colors ${
                      disabled
                        ? 'text-slate-200 cursor-not-allowed'
                        : isSelected
                        ? 'bg-brand-500 text-white font-semibold'
                        : isTodayDay
                        ? 'bg-brand-50 text-brand-700 font-semibold hover:bg-brand-100'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {day}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
